import styled from 'styled-components';
import { Box, Card, Typography } from '@mui/material';
import { COLORS } from '../../Styles/Colors';

export const VendingSectionCard = styled(Card)`
  && {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border: 1px solid ${COLORS.lightGray}40;
    border-radius: 12px;
    height: 100%;
  }
`;

export const VendingSectionTitle = styled(Typography)`
  && {
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${COLORS.teal};
    margin-bottom: 16px;
  }
`;

export const VendingDetailRow = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid ${COLORS.lightGray}35;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-of-type {
    padding-top: 0;
  }
`;

export const VendingDetailLabel = styled(Typography)`
  && {
    font-size: 0.875rem;
    color: ${COLORS.lightGray};
    flex-shrink: 0;
  }
`;

export const VendingDetailValue = styled(Typography)<{ $color?: string }>`
  && {
    font-size: 0.875rem;
    font-weight: 600;
    color: ${({ $color }) => $color || COLORS.darkGray};
    text-align: right;
  }
`;

export const VendingKpiCard = styled(Card)`
  && {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border: 1px solid ${COLORS.lightGray}40;
    border-radius: 12px;
    height: 100%;
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
    }
  }
`;

export const InventoryStatGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const InventoryStatPill = styled(Box)<{ $variant: 'good' | 'low' | 'empty' | 'total' }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 14px 8px;
  border-radius: 8px;
  background: ${({ $variant }) => {
    switch ($variant) {
      case 'good':
        return `${COLORS.success}18`;
      case 'low':
        return `${COLORS.orange}18`;
      case 'empty':
        return `${COLORS.red}12`;
      default:
        return `${COLORS.teal}10`;
    }
  }};
`;

export const ActivityItem = styled(Box)`
  display: grid;
  grid-template-columns: 72px 1fr 72px 52px;
  gap: 12px;
  align-items: center;
  padding: 12px 12px 12px 16px;
  border-left: 3px solid ${COLORS.orange};
  background: ${COLORS.white};
  border-radius: 0 8px 8px 0;
  margin-bottom: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  transition: background-color 0.2s ease;

  &:hover {
    background: #fafbfc;
  }

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 2px;
  }
`;

export const MetaTimestampGrid = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px;
  border-radius: 8px;
  background: ${COLORS.white};
  border: 1px solid ${COLORS.lightGray}40;
  min-width: 240px;
`;

export const MetaTimestampRow = styled(Box)`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  font-size: 0.8125rem;
  line-height: 1.4;
`;

export const FilterChipRow = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

export const RestockDialogLine = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 14px 0;
  border-bottom: 1px solid ${COLORS.lightGray}35;

  &:last-child {
    border-bottom: none;
  }
`;

export const AutoMapPreviewRow = styled(Box)<{ $selected?: boolean; $conflict?: boolean }>`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px 16px;
  border-radius: 8px;
  border: 1px solid ${({ $conflict }) => ($conflict ? `${COLORS.orange}60` : `${COLORS.lightGray}40`)};
  background: ${({ $selected, $conflict }) => {
    if ($conflict) return `${COLORS.orange}08`;
    if ($selected) return `${COLORS.teal}08`;
    return COLORS.white;
  }};
  margin-bottom: 8px;
  transition: background-color 0.2s ease, border-color 0.2s ease;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const VendingTealTabs = styled(Box)`
  .MuiTabs-indicator {
    background-color: ${COLORS.teal};
    height: 3px;
    border-radius: 3px 3px 0 0;
  }

  .MuiTab-root {
    font-weight: 600;
    color: ${COLORS.lightGray};
    text-transform: none;
    min-height: 44px;

    &.Mui-selected {
      color: ${COLORS.teal};
    }
  }
`;

/** Compact neutral table styling for mapping dialogs */
export const compactDialogTableSx = {
  '& .MuiTableCell-root': {
    py: 0.5,
    px: 1.25,
    fontSize: '0.8125rem',
    lineHeight: 1.35,
    color: COLORS.darkGray,
    borderColor: `${COLORS.lightGray}35`,
  },
  '& .MuiTableCell-head': {
    py: 0.75,
    fontWeight: 600,
    fontSize: '0.6875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: COLORS.lightGray,
    backgroundColor: '#f8f9fa',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: '#fafbfc',
  },
  '& .MuiTableRow-root.Mui-selected': {
    backgroundColor: `${COLORS.teal}0a !important`,
  },
  '& .MuiTableRow-root.Mui-selected:hover': {
    backgroundColor: `${COLORS.teal}12 !important`,
  },
};
